/**
 * @file PlanVersionHistory.jsx
 * @module ui/src/components/tenant-admin/PlanVersionHistory
 * @description
 *   Renders the administrative version lineage of an atomic compensation plan
 *   document, preserving effective windows and governance events for historical
 *   evidence and field-level audit inspection.
 *
 * @version 1.0.0
 * @since S755
 * @author data-fit SME Build · Reviewed: Claude S755
 *
 * @architecture
 *   Locked decisions : #1, #25
 *   Temporal invariants : INV-3, INV-9
 *   Design principles : DP-1, DP-2, DP-5, DP-14
 *   Cross-cutting contracts : XC-7
 *
 * @domain-rules
 *   - Each plan version is preserved as an immutable governance document artifact once used.
 *   - planStatus is a workflow field and never determines temporal applicability.
 *   - Effective applicability comes only from effectiveFrom and effectiveTo supplied by the server.
 *   - Missing effective-date fields never fall back to fiscal period identifiers.
 *   - Superseded versions remain visible because they may govern historical economic events within their effective windows.
 *   - Audit details load lazily per expanded version to avoid N-at-once audit requests.
 *   - Event state is always named explicitly in text.
 *
 * @session-6-migration-flags
 *   - effective_from on plan — pending m_tc_01
 *   - effective_to on plan — pending m_tc_01; nullable/open-ended
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group C batch delivery
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} FieldChange
 * @property {string} section
 * @property {string} field
 * @property {string} before
 * @property {string} after
 */

/**
 * @typedef {Object} VersionRecord
 * @property {number} version
 * @property {string} effectiveFrom
 * @property {string|null} effectiveTo
 * @property {string} planStatus
 * @property {string} submittedBy
 * @property {string} submittedAt
 * @property {string|null} approvedBy
 * @property {string|null} approvedAt
 * @property {string|null} amendmentReason
 * @property {FieldChange[]} fieldChanges
 * @property {'created'|'approved'|'amended'|'superseded'|'withdrawn'|'cancelled'} eventType
 */

/**
 * @typedef {Object} PlanVersionHistoryProps
 * @property {number} planId
 * @property {number} currentVersion
 * @property {boolean} [compact=false]
 */

const EVENT_LABELS = Object.freeze({
  created: 'Created',
  approved: 'Approved',
  amended: 'Amended',
  superseded: 'Superseded',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
});

const STATUS_LABELS = Object.freeze({
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  superseded: 'Superseded',
  withdrawn: 'Withdrawn',
});

const styles = {
  root: {
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    color: 'rgb(var(--color-text))',
  },
  section: {
    border: '1px solid rgb(var(--color-border))',
    borderRadius: 8,
    background: 'rgb(var(--color-surface))',
  },
  compactHeader: {
    padding: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  item: {
    padding: 16,
    borderTop: '1px solid rgb(var(--color-border))',
  },
  firstItem: {
    padding: 16,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  badges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
  },
  badge: {
    display: 'inline-flex',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 12,
    fontWeight: 600,
  },
  muted: {
    color: 'rgb(var(--color-text-muted))',
  },
  metadata: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 10,
    marginTop: 12,
  },
  label: {
    display: 'block',
    color: 'rgb(var(--color-text-muted))',
    fontSize: 12,
    marginBottom: 2,
  },
  details: {
    marginTop: 14,
    padding: 12,
    background: 'rgb(var(--color-surface-muted))',
    borderRadius: 6,
  },
  change: {
    padding: '10px 0',
    borderBottom: '1px solid rgb(var(--color-border))',
  },
};

/**
 * WHY: Dates are display formatting only. Temporal meaning remains exactly what
 * the server supplied in effectiveFrom/effectiveTo.
 */
function formatDate(value) {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * WHY: Governance timestamps are formatted for administrator readability but
 * are not used to derive effective applicability.
 */
function formatDateTime(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * WHY: Pre-migration records can legitimately lack effective_from/effective_to.
 * We explicitly surface that absence rather than substituting fiscal period or
 * workflow status as a false temporal proxy (INV-3).
 */
function formatEffectiveWindow(record) {
  if (!record.effectiveFrom) {
    return 'Effective period: not yet recorded';
  }

  const from = formatDate(record.effectiveFrom);

  if (!record.effectiveTo) {
    return `${from} → Open-ended`;
  }

  return `${from} → ${formatDate(record.effectiveTo)}`;
}

/**
 * @param {PlanVersionHistoryProps} props
 * @returns {JSX.Element}
 */
export default function PlanVersionHistory({
  planId,
  currentVersion,
  compact = false,
}) {
  // WHY: Version records are loaded once per plan because the complete lineage
  // is necessary evidence; superseded versions are intentionally retained.
  const [versions, setVersions] = useState([]);

  // WHY: Initial history loading is distinct from lazy audit loading so the
  // timeline remains usable without waiting for field-level audit details.
  const [loading, setLoading] = useState(true);

  // WHY: History load failure must be explicit on a governance surface rather
  // than being interpreted as "no prior versions".
  const [error, setError] = useState(null);

  // WHY: Only one detailed version is expanded at a time to keep governance
  // complexity progressively disclosed (DP-5).
  const [expandedVersion, setExpandedVersion] = useState(null);

  // WHY: Audit requests are independently tracked because they happen only
  // after a user requests field-level evidence.
  const [auditLoading, setAuditLoading] = useState(false);

  // WHY: Loaded audit evidence is cached by version so reopening a version does
  // not generate unnecessary repeat requests.
  const [auditData, setAuditData] = useState({});

  // WHY: Compact embedding must itself remain collapsible so C-51 can include
  // full lineage evidence without making governance metadata excessively dense.
  const [compactExpanded, setCompactExpanded] = useState(false);

  // WHY: A ref lets us abort whichever lazy audit request is outstanding when
  // another version is selected or when this component unmounts.
  const auditAbortRef = useRef(null);

  // WHY: Version history is server-owned governance evidence. The effect reads
  // it on plan change and aborts stale requests so another plan's response
  // cannot populate this component after navigation.
  useEffect(() => {
    const controller = new AbortController();

    async function loadVersionHistory() {
      setLoading(true);
      setError(null);
      setVersions([]);
      setExpandedVersion(null);
      setAuditData({});

      try {
        const response = await fetch(
          `/api/v1/plans/${encodeURIComponent(planId)}/version-history`,
          { signal: controller.signal },
        );

        if (!response.ok) {
          throw new Error(
            `Version history request failed with status ${response.status}.`,
          );
        }

        const payload = await response.json();

        // WHY: EP18 spec returns {planCode, versions:[]} not a bare array.
        // payload.versions is the authoritative array; bare array kept as fallback
        // for any legacy response shape.
        const versionArray = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.versions)
            ? payload.versions
            : [];

        setVersions(versionArray);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError(
            requestError.message ||
              'Plan version history could not be loaded.',
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadVersionHistory();

    return () => {
      controller.abort();

      // WHY: Lazy audit requests must also be cancelled on unmount; otherwise
      // an audit response could attempt to update an obsolete plan surface.
      auditAbortRef.current?.abort();
    };
  }, [planId]);

  /**
   * WHY: Audit detail is loaded only when the administrator explicitly expands
   * a version. This avoids one audit request per historical version on mount.
   */
  const loadAuditForVersion = async (version) => {
    if (auditData[version]) {
      return;
    }

    auditAbortRef.current?.abort();
    const controller = new AbortController();
    auditAbortRef.current = controller;

    setAuditLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/v1/plans/${encodeURIComponent(planId)}/audit?version=${encodeURIComponent(version)}`,
        { signal: controller.signal },
      );

      if (!response.ok) {
        throw new Error(
          `Audit request failed with status ${response.status}.`,
        );
      }

      const payload = await response.json();

      // WHY: EP19 spec returns {auditEntries:[], pagination:{}} not a bare array.
      // payload.auditEntries is the authoritative array; bare array kept as fallback.
      const entryArray = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.auditEntries)
          ? payload.auditEntries
          : [];

      setAuditData((existing) => ({
        ...existing,
        [version]: entryArray,
      }));
    } catch (requestError) {
      if (requestError.name !== 'AbortError') {
        setError(
          requestError.message ||
            `Audit details for version ${version} could not be loaded.`,
        );
      }
    } finally {
      if (!controller.signal.aborted) {
        setAuditLoading(false);
      }
    }
  };

  /**
   * WHY: compact mode deliberately does not expose field-level expansion; C-51
   * uses it only for lineage context while the full history surface provides
   * audit drill-down.
   */
  const handleVersionToggle = async (version) => {
    if (compact) {
      return;
    }

    if (expandedVersion === version) {
      auditAbortRef.current?.abort();
      setExpandedVersion(null);
      return;
    }

    setExpandedVersion(version);
    await loadAuditForVersion(version);
  };

  // WHY: Loading state uses a skeleton rather than an empty timeline so absence
  // of evidence is never confused with evidence still being retrieved.
  if (loading) {
    return (
      <div style={styles.root} aria-busy="true" aria-label="Loading version history">
        <div className="skeleton" style={{ height: 72, borderRadius: 8 }} />
      </div>
    );
  }

  // WHY: A failed governance read is materially different from a plan with no
  // history and must be named explicitly.
  if (error && versions.length === 0) {
    return (
      <div
        className="modifier-manager-inline-error"
        role="alert"
        style={styles.root}
      >
        {error}
      </div>
    );
  }

  const historyContent = (
    <div role="list" style={styles.list}>
      {versions.length === 0 ? (
        <div
          role="listitem"
          className="modifier-manager-readonly-note"
          style={{ padding: 16 }}
        >
          No version history has been recorded for this plan.
        </div>
      ) : (
        versions.map((record, index) => {
          const eventLabel = EVENT_LABELS[record.eventType] || record.eventType;
          const workflowLabel =
            STATUS_LABELS[record.planStatus] || record.planStatus;
          const detailId = `plan-version-${planId}-${record.version}-audit`;
          const isExpanded = expandedVersion === record.version;
          const changes = auditData[record.version] || record.fieldChanges || [];

          return (
            <article
              role="listitem"
              key={`${record.version}-${record.eventType}`}
              style={index === 0 ? styles.firstItem : styles.item}
            >
              <div style={styles.row}>
                <div>
                  <div style={styles.badges}>
                    <span style={styles.badge}>V{record.version}</span>
                    <span style={styles.badge}>{eventLabel}</span>
                    <span style={styles.badge}>
                      Workflow: {workflowLabel}
                    </span>

                    {record.version === currentVersion && (
                      <span style={styles.badge}>Open document version</span>
                    )}
                  </div>

                  <p style={{ margin: '8px 0 0' }}>
                    {formatEffectiveWindow(record)}
                  </p>
                </div>

                {!compact && (
                  <button
                    type="button"
                    className="text-button"
                    aria-expanded={isExpanded}
                    aria-controls={detailId}
                    onClick={() => handleVersionToggle(record.version)}
                  >
                    {isExpanded ? 'Hide field changes' : 'View field changes'}
                  </button>
                )}
              </div>

              <div style={styles.metadata}>
                <div>
                  <span style={styles.label}>Submitted</span>
                  <span>
                    {record.submittedBy && record.submittedAt
                      ? `${record.submittedBy} · ${formatDateTime(record.submittedAt)}`
                      : 'Not recorded'}
                  </span>
                </div>

                {!compact && (
                  <div>
                    <span style={styles.label}>Approved</span>
                    <span>
                      {record.approvedBy && record.approvedAt
                        ? `${record.approvedBy} · ${formatDateTime(record.approvedAt)}`
                        : 'Not approved'}
                    </span>
                  </div>
                )}
              </div>

              {record.amendmentReason && (
                <div style={{ marginTop: 10 }}>
                  <span style={styles.label}>Amendment reason</span>
                  <span>{record.amendmentReason}</span>
                </div>
              )}

              {!compact && isExpanded && (
                <div id={detailId} style={styles.details}>
                  <strong>Field-level changes</strong>

                  {auditLoading && !auditData[record.version] ? (
                    <div
                      className="skeleton"
                      style={{ height: 48, marginTop: 10, borderRadius: 6 }}
                      aria-label={`Loading audit details for version ${record.version}`}
                    />
                  ) : changes.length === 0 ? (
                    <p style={styles.muted}>
                      No field-level changes are recorded for this version.
                    </p>
                  ) : (
                    changes.map((change, changeIndex) => (
                      <div
                        key={`${change.section}-${change.field}-${changeIndex}`}
                        style={styles.change}
                      >
                        <strong>
                          {change.section} · {change.field}
                        </strong>
                        <div style={styles.metadata}>
                          <div>
                            <span style={styles.label}>Before</span>
                            <span>{change.before || '—'}</span>
                          </div>
                          <div>
                            <span style={styles.label}>After</span>
                            <span>{change.after || '—'}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </article>
          );
        })
      )}
    </div>
  );

  // WHY: C-51 needs a lower-density version-history embedding. Compact mode
  // preserves the full lineage but hides it behind a semantic disclosure.
  if (compact) {
    return (
      <section style={{ ...styles.root, ...styles.section }}>
        <div style={styles.compactHeader}>
          <div>
            <strong>Version lineage</strong>
            <div style={styles.muted}>
              {versions.length} recorded version
              {versions.length === 1 ? '' : 's'}
            </div>
          </div>

          <button
            type="button"
            className="text-button"
            aria-expanded={compactExpanded}
            aria-controls={`compact-plan-history-${planId}`}
            onClick={() => setCompactExpanded((expanded) => !expanded)}
          >
            {compactExpanded ? 'Hide lineage' : 'Show lineage'}
          </button>
        </div>

        {compactExpanded && (
          <div id={`compact-plan-history-${planId}`}>
            {historyContent}
          </div>
        )}

        {error && (
          <div
            className="modifier-manager-inline-error"
            role="alert"
            style={{ margin: 12 }}
          >
            {error}
          </div>
        )}
      </section>
    );
  }

  return (
    <section style={{ ...styles.root, ...styles.section }}>
      <header style={{ padding: 16 }}>
        <h3 style={{ margin: 0 }}>Plan version history</h3>
        <p style={{ ...styles.muted, marginBottom: 0 }}>
          Governance lineage and effective applicability are shown separately.
          Superseded versions remain visible because they can govern historical
          economic events.
        </p>
      </header>

      {historyContent}

      {error && (
        <div
          className="modifier-manager-inline-error"
          role="alert"
          style={{ margin: 12 }}
        >
          {error}
        </div>
      )}
    </section>
  );
}

PlanVersionHistory.propTypes = {
  planId: PropTypes.number.isRequired,
  currentVersion: PropTypes.number.isRequired,
  compact: PropTypes.bool,
};

/**
 * @example
 * <PlanVersionHistory
 *   planId={418}
 *   currentVersion={3}
 * />
 *
 * @example
 * <PlanVersionHistory
 *   planId={418}
 *   currentVersion={3}
 *   compact
 * />
 */

/*
 * DOMAIN COMPLIANCE CHECK
 * - Locked decision #1: versions are rendered as lineage of one atomic plan document.
 * - Locked decision #25: superseded versions remain fully visible.
 * - INV-3: planStatus is displayed only as "Workflow: ..." and never drives effective applicability.
 * - INV-9: historical records are read-only evidence; this component exposes no mutation.
 * - DP-1: effective windows and workflow status are separate fields.
 * - Missing effectiveFrom produces "Effective period: not yet recorded"; fiscal period is never used as fallback.
 * - Audit requests are lazy per expanded version rather than N-at-once on mount.
 * - Both history and audit network reads use AbortController.
 * - DP-14 / XC-7: event and workflow states are always text-labelled.
 */

/*
 * SESSION 6 MIGRATION FLAGS
 * - plan.effective_from — pending m_tc_01
 * - plan.effective_to — pending m_tc_01; nullable/open-ended
 */

/*
 * ACCESSIBILITY NOTES
 * - Version timeline uses role="list" and each record uses role="listitem".
 * - Full-mode expansion buttons expose aria-expanded and aria-controls.
 * - Compact history disclosure exposes aria-expanded and aria-controls.
 * - Event and workflow badges contain visible text rather than color-only state.
 * - Loading regions expose aria-busy or descriptive accessible labels.
 * - API errors use role="alert".
 */